/**
 * Core-100 CORE-AZ (owner direction 5): the shop can see WHICH reminders were not sent and WHY.
 *
 * Reminders are suppressed when consent cannot be established (no consent on record, a STOP, ticket
 * texts turned off), and for operational reasons (ticket deleted, vehicle already here). The analytics
 * card only counted them; this lists the last 30 days grouped by reason, so a shop can tell "we never
 * asked these customers" from "they asked us to stop".
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { clock } from '@/utils/clock';

export interface SuppressedReasonRow { reason: string; count: number; channels: string[] }

/** Pure: group suppressed log rows by reason, most frequent first. */
export function groupSuppressedReminders(
  rows: ReadonlyArray<{ suppression_reason: string | null; channel: string | null }>,
): SuppressedReasonRow[] {
  const by = new Map<string, { count: number; channels: Set<string> }>();
  for (const r of rows) {
    const reason = r.suppression_reason?.trim() || 'Reason not recorded';
    const e = by.get(reason) ?? { count: 0, channels: new Set<string>() };
    e.count += 1;
    if (r.channel) e.channels.add(r.channel);
    by.set(reason, e);
  }
  return [...by.entries()]
    .map(([reason, e]) => ({ reason, count: e.count, channels: [...e.channels].sort() }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

export function SuppressedReminderReasons() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['suppressed-reminder-reasons', shopId],
    enabled: !!shopId,
    queryFn: async () => {
      const since = new Date(clock.now().getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: rows, error } = await supabase
        .from('reminder_log')
        .select('suppression_reason, channel')
        .eq('shop_id', shopId!)
        .eq('status', 'suppressed')
        .gte('created_at', since)
        .limit(5000);
      if (error) throw error;
      return groupSuppressedReminders((rows ?? []) as never);
    },
  });

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="suppressed-reminder-reasons">
      <p className="text-sm font-medium">Reminders not sent (last 30 days)</p>
      {isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
      {isError && <p role="alert" className="text-xs text-destructive">Could not load suppressed reminders.</p>}
      {data && data.length === 0 && <p className="text-xs text-muted-foreground">No reminders were held back.</p>}
      {data && data.length > 0 && (
        <ul className="space-y-1">
          {data.map((r) => (
            <li key={r.reason} className="flex items-start justify-between gap-3 text-sm">
              <span>{r.reason}{r.channels.length > 0 && <span className="text-xs text-muted-foreground"> · {r.channels.join(', ')}</span>}</span>
              <span className="font-mono tabular-nums">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Text reminders go only to customers who opted in on the ticket and as a customer, and who have not replied STOP.
      </p>
    </div>
  );
}
